import BootstrapTable from 'react-bootstrap-table-next';
import cn from 'classnames';
import { isNumber } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import paginationFactory from 'react-bootstrap-table2-paginator';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';
import { useBufferByVariant } from '../../../hooks/useBufferByVariantPermission';
import { useIsSupplierAdmin } from '../../../hooks/useIsSupplierAdmin';

import { datetimeFormatter } from '../../../utils/formatHelper';
import ProductFilter from '../../filters/ProductFilter';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { sizePerPageRenderer, paginationTotalRenderer } from '../../../utils/tableHelper';
import { StaticFilters } from '../../../constants/staticFilters';
import { CustomBuffersLabel } from '../../../constants/customBuffers';
import { setOffset, setNumberOfRecordsPerPage, setSelectedVariants } from '../../../store/actions/supplierInventory';
import {
  setProductFiltersOrder,
  setProductFiltersOrderField,
  setProductFiltersSearch,
  setProductFiltersSetFilters,
} from '../../../store/actions/productFilters';

import { CymbioSpinner } from '../../common/Spinner';
import { SearchInventory } from './searchInventory/SearchInventory';

import styles from './SupplierInventory.module.scss';
import { isFeatureFlagEnabledForUserOrSupplier } from '../../../utils/user-based-feature-flag';
import { InfoTitle } from '../../common/InfoTooltip';
import { alertError } from '../../../store/actions/alert';
import { MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS } from '../../../store/constants/selectedVariants';
import { useInventoryManualUpdateByPopup } from '../../../hooks/useInventoryManualUpdatePermission';

const ProductFilterMemo = memo(() => {
  const dispatch = useDispatch();
  const isBufferEnabled = useBufferByVariant();

  return (
    <div className="column_filter">
      <SearchInventory />
      <ProductFilter
        staticFilters={[
          StaticFilters.VARIANTS_HAS_INVENTORY,
          StaticFilters.VARIANT_IS_APPROVED,
          StaticFilters.ACTIVE_PRODUCTS,
          ...(isBufferEnabled ? [StaticFilters.CUSTOM_BUFFERS] : []),
        ]}
        setFilter={(filters) => {
          dispatch(setProductFiltersSetFilters(filters));
          dispatch(setOffset(0));
        }}
        setFilterSearch={(key, value) => dispatch(setProductFiltersSearch(key, value))}
        showCount={false}
      />
    </div>
  );
});

function SupplierInventory(props) {
  const { data, offset, count, perPage, loading, selectedVariants } = useSelector((state) => state.supplierInventory);
  const { orderField, order } = useSelector((state) => state.productFilters);
  const isSupplierAdmin = useIsSupplierAdmin();
  const dispatch = useDispatch();

  const variantBufferFeatureFlagConfig = useBufferByVariant();
  const isVariantBufferEnabled = isFeatureFlagEnabledForUserOrSupplier(variantBufferFeatureFlagConfig);

  const variantManualUpdateFeatureFlagConfig = useInventoryManualUpdateByPopup();
  const isInventoryManualUpdateEnabled = isFeatureFlagEnabledForUserOrSupplier(variantManualUpdateFeatureFlagConfig);

  const canMultiSelectProducts = (isVariantBufferEnabled || isInventoryManualUpdateEnabled) && isSupplierAdmin;

  const onPageChange = React.useCallback((page, sizePerPage) => {
    dispatch(setOffset((page - 1) * sizePerPage));
    // eslint-disable-next-line
  }, []);

  const onSizePerPageChange = React.useCallback((sizePerPage) => {
    dispatch(setOffset(0));
    dispatch(setNumberOfRecordsPerPage(sizePerPage));
    // eslint-disable-next-line
  }, []);

  const onSortChange = React.useCallback((name, orderType) => {
    dispatch(setProductFiltersOrderField(name));
    dispatch(setProductFiltersOrder(orderType));
    // eslint-disable-next-line
  }, []);

  const tableSortCaret = React.useCallback((direction) => {
    const classes = ['ic-sort', direction === 'asc' ? 'asc' : 'desc'];

    if (direction) {
      classes.push('selected');
    }

    return <span className={cn(classes)} />;
  }, []);

  const noDataIndication = React.useCallback(() => `There is no data to display`, []);

  const mixpanel = useMixpanel();
  const reportBi = (event) => {
    reportBusinessActionToMixpanel(mixpanel, event);
  };

  const onTableChange = React.useCallback((type, newstate) => {
    const eventName = `inventory-page-sort-table-by-${newstate.sortField}`;
    reportBi(eventName);
    // eslint-disable-next-line
  }, []);

  const getColumns = () => {
    const columns = [
      {
        text: 'Id',
        dataField: 'id',
        hidden: true,
      },
      {
        text: 'Model',
        dataField: 'model',
        sort: true,
        onSort: onSortChange,
      },
      {
        text: 'SKU',
        dataField: 'sku',
        sort: true,
        onSort: onSortChange,
      },
      {
        text: 'Name',
        dataField: 'title',
        sort: true,
        onSort: onSortChange,
      },
      {
        text: 'UPC',
        dataField: 'upc',
        sort: true,
        onSort: onSortChange,
      },
      {
        text: 'EAN',
        dataField: 'ean',
        onSort: onSortChange,
        sort: true,
      },
      {
        text: 'Total Units',
        dataField: 'available_inventory',
        sort: true,
        onSort: onSortChange,
      },
    ];
    if (isVariantBufferEnabled) {
      columns.push({
        text: 'SKU-Level Buffer',
        sort: true,
        onSort: onSortChange,
        dataField: 'variant_inventory_buffer',
        formatter: (cell, row, rowIndex) =>
          isNumber(cell) ? (
            <OverlayTrigger
              key={rowIndex}
              placement="top"
              overlay={
                <Tooltip>
                  <span>Custom Buffer</span>
                </Tooltip>
              }
            >
              <label>{cell}</label>
            </OverlayTrigger>
          ) : (
            <>{CustomBuffersLabel.DEFAULT_BUFFERS}</>
          ),
      });
    }

    columns.push({
      text: 'Inventory Amt. Last Updated',
      dataField: 'last_inventory_update',
      sort: true,
      onSort: onSortChange,
      headerFormatter: (column, colIndex, components) => (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              <span>The last time the inventory amount was updated from an order or the warehouse</span>
            </Tooltip>
          }
        >
          <label className={styles.sortable}>
            Inventory Amt. <div>Last Updated {components.sortElement}</div>
          </label>
        </OverlayTrigger>
      ),
      formatter: (cell) => (cell ? datetimeFormatter(cell) : ''),
      classes: 'clickable',
    });
    return columns;
  };

  const handleVariantsSelect = (rows, isSelecting) => {
    const rowIds = rows.map((variant) => variant.id);
    const rowObjectIds = rows.map((variant) => variant.object_id);
    if (isSelecting) {
      if (selectedVariants?.ids?.length === MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS) {
        dispatch(alertError(`Maximum ${MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS} selected inventory items`));
        return;
      }
      dispatch(
        setSelectedVariants({
          variantIds: [...selectedVariants.ids, ...rowIds],
          variantObjectIds: [...selectedVariants.objectIds, ...rowObjectIds],
          variantsWithDetails: [...selectedVariants.variantsWithDetails, ...rows],
        }),
      );
    } else {
      const variantIds = selectedVariants.ids.filter((variantId) => !rowIds.includes(variantId));
      const objectIds = selectedVariants.objectIds.filter((variantObjectId) => !rowObjectIds.includes(variantObjectId));
      const selectedVariantsWithDetails = selectedVariants.variantsWithDetails.filter(
        (variant) => !rowIds.includes(variant.id),
      );
      dispatch(
        setSelectedVariants({
          variantIds: variantIds,
          variantObjectIds: objectIds,
          variantsWithDetails: selectedVariantsWithDetails,
        }),
      );
    }
  };

  const handleOnSelect = (row, isSelecting) => {
    handleVariantsSelect([row], isSelecting);
  };

  const handleOnSelectAll = (isSelecting, rows) => {
    if (selectedVariants?.ids?.length > 0) {
      dispatch(setSelectedVariants());
      return;
    }

    handleVariantsSelect(rows, isSelecting);
  };

  const selectRow = {
    mode: 'checkbox',
    selected: selectedVariants.ids,
    onSelect: handleOnSelect,
    onSelectAll: handleOnSelectAll,
    clickOnSelect: false,
    nonSelectable:
      selectedVariants?.ids?.length === MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS
        ? data?.map((row) => row.id).filter((id) => !selectedVariants.ids.includes(id))
        : [],
    selectionHeaderRenderer: () => {
      const amountOfSelectedVariants = selectedVariants?.ids?.length || 0;
      const isIndeterminate =
        amountOfSelectedVariants > 0 && amountOfSelectedVariants < MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS;
      const isAllSelected =
        amountOfSelectedVariants === data?.length || amountOfSelectedVariants === MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS;

      return (
        <InfoTitle content={`Selected variants: ${amountOfSelectedVariants}`}>
          <div style={{ display: 'inline-block', paddingBottom: '10px', paddingRight: '3px' }}>
            <label className={styles.modernCheckbox} onClick={(e) => e.preventDefault()}>
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={() => {}} // empty onChange here because onSelectAll takes care of the change
                className={styles.checkboxInput}
              />
              <div className={cn(styles.checkboxIcon, { [styles.indeterminate]: isIndeterminate })} />
            </label>
          </div>
        </InfoTitle>
      );
    },
    selectionRenderer: ({ mode, checked, rowIndex }) => {
      const rowId = data[rowIndex]?.id;
      const isDisabled = !checked && selectedVariants.ids?.length === MAXIMUM_AMOUNT_OF_SELECTED_VARIANTS;
      const isNonSelectable = selectRow.nonSelectable.includes(rowId);

      return (
        <label className={styles.modernCheckbox} onClick={(e) => e.preventDefault()}>
          <input
            type={mode}
            checked={checked}
            disabled={isDisabled || isNonSelectable}
            onChange={() => {}} // empty onChange here because the onSelect takes care of the change
            className={styles.checkboxInput}
          />
          <div className={styles.checkboxIcon} />
        </label>
      );
    },
    style: { backgroundColor: 'transparent' },
  };

  const noBufferSelectRowOptions = {
    hideSelectColumn: true,
    mode: 'radio',
    clickToSelect: true,
    onSelect: props.onOpenHistoryLog,
  };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      props.onOpenHistoryLog(row);
    },
  };

  return (
    <div className="row vertical-indent">
      <ProductFilterMemo />

      <div className={cn('column_products', styles.table)}>
        {data === null || loading.products ? (
          <CymbioSpinner />
        ) : (
          <BootstrapTable
            columns={getColumns()}
            data={data}
            keyField="id"
            selectRow={canMultiSelectProducts ? selectRow : noBufferSelectRowOptions}
            rowEvents={canMultiSelectProducts ? rowEvents : {}}
            printable
            striped
            hover
            bordered={false}
            pagination={paginationFactory({
              page: offset / perPage + 1,
              sizePerPage: perPage,
              sizePerPageList: [20, 50, 100],
              onPageChange: onPageChange,
              onSizePerPageChange: onSizePerPageChange,
              totalSize: count,
              showTotal: true,
              sizePerPageRenderer,
              paginationTotalRenderer,
            })}
            remote={{
              pagination: true,
              sort: true,
            }}
            defaultSorted={[
              {
                dataField: orderField || 'sku',
                order: order || 'desc',
              },
            ]}
            defaultSortDirection={order || 'desc'}
            sort={{
              sortCaret: tableSortCaret,
            }}
            onTableChange={onTableChange ?? undefined}
            noDataIndication={noDataIndication}
          />
        )}
      </div>
    </div>
  );
}

export default SupplierInventory;

SupplierInventory.defaultProps = {
  isLoadingProducts: false,
};

SupplierInventory.propTypes = {
  onOpenHistoryLog: PropTypes.func.isRequired,
};
